import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Pencil,
  Trash2,
  Plus,
  Download,
  Copy,
  Settings2,
  FilterX,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import PortfolioBanner from '../components/PortfolioBanner';
import SubscriptionReports from '../components/SubscriptionReports';
import RevenueByMonth from '../components/RevenueByMonth';
import {
  useSubscriptions,
  usePortfolioTotals,
  useSubscription,
  useUpdateSubscription,
  useDeleteSubscription,
} from '../hooks';
import { CreateSubscriptionModal, SubscriptionFormModal, SubscriptionDeleteDialog } from '../components/modals';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';
import type { ColumnDef } from '../utils/csvExport';
import type {
  V4Subscription,
  V4SubscriptionsFilters,
  UpdateSubscriptionV4Input,
} from '../types';
import {
  computeDuration,
  getStatusVariant,
  SUBSCRIPTION_STATUSES,
} from '../types';

// =============================================================================
// Column Defaults (module-level, for visibility init before t() is available)
// =============================================================================

const COLUMN_DEFAULTS: Record<string, boolean> = {
  contract_number: true,
  name: true,
  status: true,
  account: true,
  sales_manager: true,
  contractual_manager: true,
  ssr_qty: true,
  cal_qty: true,
  lte_qty: true,
  start_date: true,
  end_date: true,
  duration: true,
  invoices: false,
  currency: false,
  payment_terms: false,
  annual_value: true,
  total_value: true,
  actions: true,
};

// =============================================================================
// localStorage Keys
// =============================================================================

const FILTER_STORAGE_KEY = 'atek-subscriptions-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-subscriptions-column-visibility';

// =============================================================================
// Column Visibility Helpers
// =============================================================================

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...COLUMN_DEFAULTS, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return { ...COLUMN_DEFAULTS };
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// End Date Cell (with expired/expiring badges)
// =============================================================================

function EndDateCell({ subscription }: { subscription: V4Subscription }) {
  const { t } = useTranslation('subscriptions');
  const endDate = subscription.endDate ? new Date(subscription.endDate) : null;
  const now = new Date();
  const expired = endDate ? endDate < now : false;
  const daysUntilEnd = endDate ? Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : null;
  const expiring = daysUntilEnd !== null && daysUntilEnd <= 7 && daysUntilEnd > 0;

  return (
    <div className="flex items-center gap-2">
      <span className={expired ? 'text-destructive font-medium' : expiring ? 'text-yellow-600 font-medium' : ''}>
        {subscription.endDate ? formatDate(subscription.endDate) : '—'}
      </span>
      {expired && <Badge variant="destructive">{t('table.expired')}</Badge>}
      {expiring && !expired && <Badge variant="warning">{t('table.expiring')}</Badge>}
    </div>
  );
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Column Filter Rendering
// =============================================================================

function renderColumnFilter<T>(
  col: ColumnDef<T>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
  t: (key: string, opts?: Record<string, unknown>) => string,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? t('table.nSelected', { count: selected.length }) : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export default function SubscriptionsPage() {
  const { t } = useTranslation('subscriptions');

  // ── Column definitions (inside component for i18n) ───────────────────
  const columns: ColumnDef<V4Subscription>[] = useMemo(() => [
    {
      key: 'contract_number',
      label: t('table.contractNumber'),
      sortKey: 'contract_number',
      filterType: 'text',
      getValue: (c) => c.subscriptionNumber,
      render: (c) => <span className="font-medium">{c.subscriptionNumber}</span>,
    },
    {
      key: 'name',
      label: t('table.name'),
      sortKey: 'name',
      filterType: 'text',
      getValue: (c) => c.name || '',
      render: (c) => <span className="max-w-48 truncate block">{c.name || '—'}</span>,
    },
    {
      key: 'status',
      label: t('table.status'),
      sortKey: 'status',
      filterType: 'select',
      filterOptions: SUBSCRIPTION_STATUSES.map((s) => ({
        label: t(`status.${s}`),
        value: s,
      })),
      getValue: (c) => c.status,
      render: (c) => <Badge variant={getStatusVariant(c.status)}>{t(`status.${c.status}`)}</Badge>,
    },
    {
      key: 'account',
      label: t('table.account'),
      sortKey: 'account',
      filterType: 'text',
      getValue: (c) => c.accountName || '',
      render: (c) => <span className="max-w-40 truncate block">{c.accountName || '—'}</span>,
    },
    {
      key: 'sales_manager',
      label: t('table.salesManager'),
      filterType: 'text',
      getValue: (c) => c.salesManagerName || '',
      render: (c) => (
        <span className="text-muted-foreground">{c.salesManagerName || '—'}</span>
      ),
    },
    {
      key: 'contractual_manager',
      label: t('table.contractualManager'),
      filterType: 'text',
      getValue: (c) => c.contractualManagerName || '',
      render: (c) => (
        <span className="text-muted-foreground">{c.contractualManagerName || '—'}</span>
      ),
    },
    {
      key: 'ssr_qty',
      label: t('table.ssrQty'),
      noFilter: true,
      getValue: (c) => String(c.ssrQty ?? 0),
      render: (c) => <span className="tabular-nums">{c.ssrQty ?? 0}</span>,
      className: 'text-right w-20',
    },
    {
      key: 'cal_qty',
      label: t('table.calQty'),
      noFilter: true,
      getValue: (c) => String(c.calQty ?? 0),
      render: (c) => <span className="tabular-nums">{c.calQty ?? 0}</span>,
      className: 'text-right w-20',
    },
    {
      key: 'lte_qty',
      label: t('table.lteQty'),
      noFilter: true,
      getValue: (c) => String(c.lteQty ?? 0),
      render: (c) => <span className="tabular-nums">{c.lteQty ?? 0}</span>,
      className: 'text-right w-20',
    },
    {
      key: 'start_date',
      label: t('table.startDate'),
      sortKey: 'start_date',
      filterType: 'date-range',
      getValue: (c) => c.startDate || '',
      render: (c) => (c.startDate ? formatDate(c.startDate) : '—'),
      className: 'w-28',
    },
    {
      key: 'end_date',
      label: t('table.endDate'),
      sortKey: 'end_date',
      filterType: 'date-range',
      getValue: (c) => c.endDate || '',
      render: (c) => <EndDateCell subscription={c} />,
      className: 'w-28',
    },
    {
      key: 'duration',
      label: t('table.duration'),
      noFilter: true,
      getValue: (c) => computeDuration(c.startDate, c.endDate) || '',
      render: (c) => <span className="text-muted-foreground">{computeDuration(c.startDate, c.endDate) || '—'}</span>,
    },
    {
      key: 'invoices',
      label: t('table.invoices'),
      noFilter: true,
      getValue: (c) => String(c.invoiceCount || 0),
      render: (c) => <span className="tabular-nums">{c.invoiceCount || 0}</span>,
    },
    {
      key: 'currency',
      label: t('table.currency'),
      filterType: 'select',
      filterOptions: [
        { label: 'CAD', value: 'CAD' },
        { label: 'USD', value: 'USD' },
        { label: 'EUR', value: 'EUR' },
      ],
      getValue: (c) => c.currency || 'CAD',
      className: 'w-16',
    },
    {
      key: 'payment_terms',
      label: t('table.paymentTerms'),
      defaultVisible: false,
      filterType: 'select',
      filterOptions: [
        { label: t('paymentTerms.on-order'), value: 'on-order' },
        { label: t('paymentTerms.net-0'), value: 'net-0' },
        { label: t('paymentTerms.net-30'), value: 'net-30' },
        { label: t('paymentTerms.net-45'), value: 'net-45' },
        { label: t('paymentTerms.net-60'), value: 'net-60' },
      ],
      getValue: (c) => c.paymentTerms || '',
    },
    {
      key: 'annual_value',
      label: t('table.annualValue', 'Value / yr'),
      filterType: 'number-range',
      getValue: (c) => String(c.annualValue || 0),
      render: (c) => (c.annualValue != null && Number(c.annualValue) !== 0
        ? <span className="tabular-nums">{formatCurrency(c.annualValue, c.currency || 'CAD')}</span>
        : <span className="text-muted-foreground">—</span>),
      align: 'right',
    },
    {
      key: 'total_value',
      label: t('table.totalValue', 'Total value'),
      filterType: 'number-range',
      getValue: (c) => String(c.totalValue || 0),
      render: (c) => (c.totalValue != null && Number(c.totalValue) !== 0
        ? <span className="tabular-nums">{formatCurrency(c.totalValue, c.currency || 'CAD')}</span>
        : <span className="text-muted-foreground">—</span>),
      align: 'right',
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      align: 'right',
      className: 'w-20',
    },
  ], [t]);

  // ── Server-side state ────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [sortKey, setSortKey] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  // ── Client-side column filters ───────────────────────────────────────
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || '{}'); }
    catch { return {}; }
  });

  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Extract text column filter values for server-side search
  // (date-range values contain ">>" and selects start with "[" — skip those)
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  // Debounce column search text for server-side queries
  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  // ── Column visibility ────────────────────────────────────────────────
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  const toggleColumn = useCallback((key: string) => {
    setColumnVisibility((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      saveColumnVisibility(updated);
      return updated;
    });
  }, []);

  const isVisible = (key: string) => columnVisibility[key] !== false;
  const visibleColumns = columns.filter((col) => isVisible(col.key));

  // ── Modal state ──────────────────────────────────────────────────────
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<string | null>(null);
  const [deletingSubscription, setDeletingSubscription] = useState<V4Subscription | null>(null);

  // ── Build server filters ─────────────────────────────────────────────
  const filters: V4SubscriptionsFilters = {
    search: debouncedColumnSearch || undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };

  // ── Queries ──────────────────────────────────────────────────────────
  const subscriptionsQuery = useSubscriptions(filters);
  const portfolioTotalsQuery = usePortfolioTotals();
  const editSubscriptionQuery = useSubscription(editingSubscriptionId);

  // ── Mutations ────────────────────────────────────────────────────────
  const updateMutation = useUpdateSubscription();
  const deleteMutation = useDeleteSubscription();

  const subscriptions = subscriptionsQuery.data?.subscriptions || [];
  const total = subscriptionsQuery.data?.total || 0;
  const totalPages = Math.ceil(total / pageSize) || 1;

  // ── Client-side column filtering (refinement within page) ────────────
  const filteredSubscriptions = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return subscriptions;

    return subscriptions.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        // Date range: "from>>to" — uses Date comparison for correctness
        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const date = new Date(String(rawValue || ''));
          if (isNaN(date.getTime())) return !from && !to;
          if (from) {
            const fromDate = new Date(from);
            if (date < fromDate) return false;
          }
          if (to) {
            const toDate = new Date(to);
            toDate.setDate(toDate.getDate() + 1); // exclusive end of day
            if (date >= toDate) return false;
          }
          return true;
        }

        // Multi-select: JSON array
        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        // Number range: "min>>max"
        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        // Text: case-insensitive includes
        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      })
    );
  }, [subscriptions, columnFilters, columns]);

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(-1);
    }
    setPage(0);
  };

  const handleRowClick = (subscription: V4Subscription) => {
    setEditingSubscriptionId(subscription.id);
  };

  const handleSubscriptionCreated = (subscriptionId: string) => {
    setEditingSubscriptionId(subscriptionId);
  };

  const handleUpdateSubscription = async (data: UpdateSubscriptionV4Input) => {
    if (!editingSubscriptionId) return;
    await updateMutation.mutateAsync({ id: editingSubscriptionId, data });
  };

  const handleDeleteClick = (subscription: V4Subscription) => {
    setDeletingSubscription(subscription);
  };

  const handleDeleteConfirm = () => {
    if (!deletingSubscription) return;
    deleteMutation.mutate(deletingSubscription.id, {
      onSuccess: () => {
        setDeletingSubscription(null);
      },
    });
  };

  const handleExportCSV = () => {
    try {
      exportToCSV(filteredSubscriptions, columns, 'subscriptions');
      toast.success(t('toast.exportSuccess', { count: filteredSubscriptions.length }));
    } catch {
      toast.error(t('toast.exportError'));
    }
  };

  const handleCopy = () => {
    copyToClipboard(filteredSubscriptions, columns)
      .then(() => toast.success(t('toast.copySuccess')))
      .catch(() => toast.error(t('toast.copyError')));
  };

  // Recurring-revenue portfolio totals (S11) — aggregated server-side over the whole
  // book (the list paginates, so we cannot sum the loaded page).
  const portfolioTotals = portfolioTotalsQuery.data ?? { count: 0, byCurrency: {}, expiringSoon: 0 };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="breadcrumb flex items-center gap-1.5">
        <Link to="/erp" className="text-atek-blue hover:text-atek-blue-dark">
          {t('page.erp')}
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground">{t('page.title')}</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{t('page.title')}</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            {t('page.subtitle')}
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('page.newContract')}
        </Button>
      </div>

      {/* Recurring-revenue portfolio banner (S11) */}
      <PortfolioBanner totals={portfolioTotals} />
      <SubscriptionReports />
      {/* Expected revenue by month (#1409) */}
      <RevenueByMonth />

      {/* Main Content Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('page.allContracts')}</CardTitle>
              <CardDescription>
                {filteredSubscriptions.length < subscriptions.length
                  ? t('table.showingFiltered', { filtered: filteredSubscriptions.length, pageCount: subscriptions.length })
                  : t('table.showingOf', { pageCount: subscriptions.length, total })}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {Object.values(columnFilters).some(Boolean) && (
                <Button variant="outline" size="sm" onClick={() => setColumnFilters({})}>
                  <FilterX className="h-4 w-4 mr-1" /> Clear filters
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-1" />
                {t('table.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-1" />
                {t('table.csv')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings2 className="h-4 w-4 mr-1" />
                    {t('table.columns')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{t('table.toggleColumns')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns.filter((c) => !c.noFilter).map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={isVisible(col.key)}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {col.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {subscriptionsQuery.isLoading ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map((col) => (
                      <TableHead key={col.key}><Skeleton className="h-4 w-16" /></TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {visibleColumns.map((col) => (
                        <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : subscriptionsQuery.error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">{t('page.loadError')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    {/* ROW 1: Column filters (always visible) */}
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      {visibleColumns.map((col) =>
                        renderColumnFilter(col, columnFilters, setColumnFilter, t)
                      )}
                    </TableRow>

                    {/* ROW 2: Column labels + sort controls */}
                    <TableRow>
                      {visibleColumns.map((col) => (
                        <TableHead key={col.key} className={cn(col.className, col.align === 'right' && 'text-right')}>
                          {col.sortKey ? (
                            <SortableHeader
                              label={col.label}
                              sortKey={col.sortKey}
                              currentSortKey={sortKey}
                              sortDirection={sortDirection}
                              onSort={handleSort}
                            />
                          ) : (
                            col.label
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {filteredSubscriptions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                          {t('table.noResults')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSubscriptions.map((subscription) => (
                        <TableRow
                          key={subscription.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => handleRowClick(subscription)}
                        >
                          {visibleColumns.map((col) => (
                            <TableCell
                              key={col.key}
                              className={cn(
                                'text-sm',
                                col.align === 'right' && 'text-right tabular-nums',
                                col.className,
                              )}
                            >
                              {col.key === 'actions' ? (
                                <ActionsCell
                                  subscription={subscription}
                                  onEdit={handleRowClick}
                                  onDelete={handleDeleteClick}
                                />
                              ) : col.render ? (
                                col.render(subscription)
                              ) : (
                                String(col.getValue(subscription) ?? '') || '—'
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="border-t px-4 py-3 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {t('table.pageOf', { current: page + 1, total: totalPages })}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                    >
                      {t('table.previous')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      {t('table.next')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <CreateSubscriptionModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onCreated={handleSubscriptionCreated}
      />

      {/* Edit Modal */}
      <SubscriptionFormModal
        open={!!editingSubscriptionId}
        onOpenChange={(open) => {
          if (!open) {
            setEditingSubscriptionId(null);
          }
        }}
        contract={editingSubscriptionId ? editSubscriptionQuery.data || null : null}
        onSubmit={handleUpdateSubscription}
        onDelete={editingSubscriptionId ? () => {
          if (editSubscriptionQuery.data) {
            setDeletingSubscription(editSubscriptionQuery.data);
            setEditingSubscriptionId(null);
          }
        } : undefined}
        isSubmitting={updateMutation.isPending}
      />

      {/* Delete Dialog */}
      <SubscriptionDeleteDialog
        open={!!deletingSubscription}
        onOpenChange={(open) => {
          if (!open) setDeletingSubscription(null);
        }}
        contract={deletingSubscription}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}

// =============================================================================
// Actions Cell
// =============================================================================

function ActionsCell({
  subscription,
  onEdit,
  onDelete,
}: {
  subscription: V4Subscription;
  onEdit: (subscription: V4Subscription) => void;
  onDelete: (subscription: V4Subscription) => void;
}) {
  const { t } = useTranslation('subscriptions');

  return (
    <div className="flex items-center gap-1">
      {subscription.gdriveFolderId && (
        <a
          href={`https://drive.google.com/drive/folders/${subscription.gdriveFolderId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-muted-foreground hover:text-primary"
          title={t('table.googleDrive')}
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={(e) => { e.stopPropagation(); onEdit(subscription); }}
        title={t('table.editContract')}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive"
        onClick={(e) => { e.stopPropagation(); onDelete(subscription); }}
        title={t('table.deleteContract')}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
